import React, { useCallback } from 'react';
import {
  Checkbox,
  FormControlLabel,
  FormControlLabelProps,
  FormControl,
  FormHelperText,
  FormGroup,
  CheckboxProps as MuiCheckboxProps,
} from '@mui/material';
import { RadioButtonUncheckedIcon, CheckCircleIcon } from '../../../assets';
import { useStyles } from '../../../themes/muiTheme';

interface CheckboxItem {
  checkboxProps?: CheckboxProps;
  labelProps?: Partial<FormControlLabelProps>;
  className?: string;
  useCustomValue?: boolean;
  useMobileIcons?: boolean;
}

interface FormikProps {
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  values: Record<string, any>;
  touched?: Record<string, any>;
  errors?: Record<string, any>;
}

interface CheckboxProps extends Omit<MuiCheckboxProps, 'onChange'> {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface CheckboxInputProps {
  name?: string;
  formikProps?: FormikProps;
  checkboxes?: CheckboxItem[];
  row?: boolean;
  className?: string;
  useCustomValue?: boolean;
  useMobileIcons?: boolean;
  checkboxProps?: CheckboxProps;
  labelProps?: Partial<FormControlLabelProps>;
}

/**
 * Material-ui Checkbox with validation
 */
export function CheckboxInput(props: CheckboxInputProps): React.ReactElement {
  const { name, formikProps, checkboxes = [], row = true } = props;

  const { touched, errors } = formikProps || {};

  return (
    <FormControl
      variant='standard'
      error={
        touched !== undefined && name !== undefined
          ? touched[name] && Boolean(errors?.[name])
          : undefined
      }
      component='fieldset'
    >
      <FormGroup row={row}>
        {checkboxes.map((c, i) => {
          return <Input key={i} {...props} {...c} />;
        })}
      </FormGroup>
      <FormHelperText>
        {touched !== undefined && name !== undefined
          ? touched[name] && errors?.[name]
          : undefined}
      </FormHelperText>
    </FormControl>
  );
}

function Input(
  props: CheckboxItem & { formikProps?: FormikProps; name?: string },
): React.ReactElement {
  const { cx } = useStyles();
  const {
    checkboxProps = {},
    labelProps = {},
    className,
    formikProps,
    useCustomValue = false,
    useMobileIcons = false,
  } = props;
  const { onBlur, onChange, name, checked, ...otherProps } = checkboxProps;
  const {
    handleChange: formikHandleChange,
    handleBlur: formikHandleBlur,
    values,
  } = formikProps || {};

  const handleBlur = useCallback(
    (e: React.FocusEvent<any>) => {
      formikHandleBlur && formikHandleBlur(e);
      onBlur && onBlur(e as any);
    },
    [formikHandleBlur, onBlur],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<any>) => {
      formikHandleChange && !useCustomValue && formikHandleChange(e);
      onChange && onChange(e as any);
    },
    [formikHandleChange, onChange, useCustomValue],
  );

  return (
    <FormControlLabel
      label=''
      control={
        <Checkbox
          className={cx(className)}
          onBlur={handleBlur}
          onChange={handleChange}
          color='primary'
          name={name}
          checked={
            formikProps !== undefined && !useCustomValue && values && name
              ? values[name]
              : checked
          }
          icon={useMobileIcons ? <RadioButtonUncheckedIcon /> : undefined}
          checkedIcon={useMobileIcons ? <CheckCircleIcon /> : undefined}
          sx={useMobileIcons ? mobileColorStyle : undefined}
          {...otherProps}
        />
      }
      {...labelProps}
    />
  );
}

const mobileColorStyle = {
  color: '#A5AABD',
  '&.Mui-checked': {
    color: '#0FC076',
  },
} as const;
